import { useInfiniteQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JustificationEnum } from '../../../../../js/api/models/JustificationEnum';
import { Need as ApiNeed } from '../../../../../js/api/models/Need';
import { UrgencyLevelEnum } from '../../../../../js/api/models/UrgencyLevelEnum';
import { StaffingService } from '../../../../../js/api/services/StaffingService';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Button } from '../../../components/button';
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerCloseButton,
  DrawerBody,
} from '../../../components/drawer';
import { PageHeader } from '../../../components/PageHeader';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useDebounce } from '../../../hooks/useDebounce';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import NeedCard from './NeedCard';
import { NeedDrawer } from './NeedDrawer';
import NeedFilters from './NeedFilters';
import { STAFFING_NEEDS_LIST_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 12; // Number of items per page

const NeedsList: React.FC = () => {
  useDocumentTitle('Needs');

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Degree',
    'Specialty',
    'ShiftRateTypes',
    'ShiftRateTypeIcons',
    'JustificationType',
    'UrgencyLevel',
  ]);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('-');
  const [showClosed, setShowClosed] = useState(false);
  const [justification, setJustification] = useState('');
  const [urgencyLevel, setUrgencyLevel] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentReason, setCurrentReason] = useState('');

  const [selectedNeedId, setSelectedNeedId] = useState<string | null>(null);
  const [selectedNeed, setSelectedNeed] = useState<ApiNeed | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const navigate = useNavigate();

  const queryKey = useMemo(
    () => [
      STAFFING_NEEDS_LIST_QUERY_KEY,
      'list',
      {
        search: debouncedSearch || undefined,
        sortBy,
        sortDirection,
        showClosed,
        justification: justification || undefined,
        urgencyLevel: urgencyLevel || undefined,
        currentStatus: currentStatus || undefined,
        currentReason: currentReason || undefined,
      },
    ],
    [
      debouncedSearch,
      sortBy,
      sortDirection,
      showClosed,
      justification,
      urgencyLevel,
      currentStatus,
      currentReason,
    ]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      const ordering = sortDirection && sortBy ? `${sortDirection}${sortBy}` : sortBy;
      return await StaffingService.staffingNeedsList({
        search: debouncedSearch || undefined,
        ordering: ordering || undefined,
        showClosed: showClosed,
        justification: (justification as JustificationEnum) || undefined,
        urgencyLevel: (urgencyLevel as UrgencyLevelEnum) || undefined,
        currentStatus: currentStatus || undefined,
        currentReason: currentReason || undefined,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
  });

  const needs = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  // Set up infinite scroll
  const loadMoreNeeds = React.useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreNeeds, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const handleResetList = () => {
    setSearch('');
    setSortBy('created_at');
    setSortDirection('-');
    setShowClosed(false);
    setJustification('');
    setUrgencyLevel('');
    setCurrentStatus('');
    setCurrentReason('');
  };

  const handleCreateNeed = () => {
    navigate('/app/organization/needs/new');
  };

  const handleNeedCardClick = (need: ApiNeed) => {
    setSelectedNeedId(need.id);
    setSelectedNeed(need); // Keep for drawer title display
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedNeedId(null);
    setSelectedNeed(null);
  };

  return (
    <>
      <PageHeader
        backUrl="/app"
        title="Needs"
        buttonHtml={
          <>
            <Button color="zinc-outline" onClick={handleResetList}>
              Reset List
            </Button>
            <Button color="brand-green-outline-on-dark" onClick={handleCreateNeed}>
              Create Need
            </Button>
          </>
        }
      />

      <main className="tw:max-w-7xl tw:mx-auto tw:mt-3 tw:px-6">
        <div className="tw:flex tw:flex-col tw:gap-3">
          <NeedFilters
            search={search}
            onSearchChange={setSearch}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortDirection={sortDirection}
            onSortDirectionChange={setSortDirection}
            showClosed={showClosed}
            onShowClosedChange={setShowClosed}
            justification={justification}
            onJustificationChange={setJustification}
            urgencyLevel={urgencyLevel}
            onUrgencyLevelChange={setUrgencyLevel}
            currentStatus={currentStatus}
            onCurrentStatusChange={setCurrentStatus}
            currentReason={currentReason}
            onCurrentReasonChange={setCurrentReason}
          />
        </div>
        <div className="tw:mt-3">
          {isLoading && needs.length === 0 && (
            <div className="tw:text-center tw:py-8">Loading needs...</div>
          )}
          {!isLoading && needs.length === 0 ? (
            <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
              {search ? 'No needs found matching your search.' : 'No needs found.'}
            </div>
          ) : (
            <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:md:grid-cols-3 tw:gap-3">
              {needs.map((need) => (
                <div
                  key={need.id}
                  onClick={() => handleNeedCardClick(need)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNeedCardClick(need);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <NeedCard
                    need={need}
                    degreeMap={codeDefinitionMaps?.Degree || {}}
                    specialtyMap={codeDefinitionMaps?.Specialty || {}}
                    shiftRateTypeMap={codeDefinitionMaps?.ShiftRateTypes || {}}
                    shiftRateTypeIconMap={codeDefinitionMaps?.ShiftRateTypeIcons || {}}
                    justificationMap={codeDefinitionMaps?.JustificationType || {}}
                    urgencyLevelMap={codeDefinitionMaps?.UrgencyLevel || {}}
                  />
                </div>
              ))}
            </div>
          )}

          {hasNextPage && (
            <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
              {isFetchingNextPage && (
                <div className="tw:text-center tw:text-zinc-500">Loading more needs...</div>
              )}
            </div>
          )}

          {needs.length > 0 && (
            <div className="tw:text-center tw:text-zinc-500 tw:mt-4">
              Showing {needs.length} of {totalCount} needs
            </div>
          )}
        </div>
      </main>

      <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="2xl">
        <DrawerHeader className="tw:border-none">
          <DrawerTitle className="tw:!text-base tw:!mb-0 tw:w-full">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
              {selectedNeed && (
                <span className="tw:inline-block tw:text-brand-green-0">
                  {selectedNeed.code}
                  <div className="tw:text-sm tw:text-zinc-600">
                    {selectedNeed.facility.name}:{' '}
                    {codeDefinitionMaps?.Specialty?.[selectedNeed.specialty] ||
                      selectedNeed.specialty}
                  </div>
                </span>
              )}
              {selectedNeed && (
                <Button
                  color="brand-green-outline"
                  className="tw:shrink-0 tw:!text-sm"
                  onClick={() => navigate(`/app/organization/needs/${selectedNeed.id}/edit`)}
                >
                  <i className="far fa-arrow-up-right-from-square" />
                  Open
                </Button>
              )}
            </div>
          </DrawerTitle>
          <DrawerCloseButton />
        </DrawerHeader>
        <DrawerBody className="tw:!text-sm/6">
          {selectedNeedId && (
            <NeedDrawer
              needId={selectedNeedId}
              urgencyLevelMap={codeDefinitionMaps?.UrgencyLevel || {}}
            />
          )}
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default NeedsList;
